import { formatLevelPrice } from '../liqMap/copy';
import type { PositionType } from '../trading/types';

import type { StopCheck } from './check';
import type { Level } from './levels';

/**
 * The plain-English layer. Each sentence says the price, how many times it
 * held, and what usually happens next — nothing else.
 */

const times = (n: number) => (n === 1 ? 'once' : n === 2 ? 'twice' : `${n} times`);

/** One level, explained. */
export function explainLevel(coin: string, level: Level): string {
    const at = `$${formatLevelPrice(level.price)}`;
    const held = times(level.touches);
    if (level.kind === 'resistance') {
        return `${coin} has turned back down at ${at} ${held} in the last three days. Levels like this attract sell orders, so expect a reaction there — and a fast move up if it breaks.`;
    }
    return `${coin} has bounced at ${at} ${held} in the last three days. Levels like this attract buy orders and stops, so expect a reaction there — and a fast move down if it breaks.`;
}

/** The one-line verdict on the trader's stop. */
export function explainStop(
    check: StopCheck,
    type: PositionType,
): { tone: 'danger' | 'warn' | 'ok'; text: string } {
    if (check.kind === 'clear' || !check.level) {
        return {
            tone: 'ok',
            text:
                type === 'LONG'
                    ? 'Your stop sits clear of the supports below — it will not be caught by a test of a level.'
                    : 'Your stop sits clear of the resistances above — it will not be caught by a test of a level.',
        };
    }
    const at = `$${formatLevelPrice(check.level.price)}`;
    const held = times(check.level.touches);
    const suggestion =
        check.suggestedStop !== null
            ? ` $${formatLevelPrice(check.suggestedStop)}, just past it, is not.`
            : '';
    const what = check.level.kind === 'support' ? 'a support' : 'a resistance';
    if (check.kind === 'onLevel') {
        return {
            tone: 'danger',
            text: `Your stop-loss sits on ${what} at ${at} that has held ${held}. Stops parked on a level get hunted by the wick that tests it.${suggestion}`,
        };
    }
    return {
        tone: 'warn',
        text: `Your stop-loss is ${Math.abs(check.gapPct ?? 0).toFixed(2)}% from ${what} at ${at} that has held ${held}. The test of that level often reaches this far.${suggestion}`,
    };
}

/** What the levels are made from, so the number is trusted for the right reason. */
export function explainSource(barCount: number, updatedAt: number, now = Date.now()): string {
    const age = Math.round((now - updatedAt) / 60_000);
    const when = age < 1 ? 'just now' : `${age} min ago`;
    return `From ${barCount} fifteen-minute candles — about three days — updated ${when}.`;
}
